import * as React from 'react';
import { cn } from '@noor/lib/cn';
import { MAX_IMAGE_BYTES, partitionBySize } from '@noor/lib';
import { MenuCloseIcon, MenuImageAddIcon } from '../menu-icons';
import { useFormFieldContext } from './form-field-context';

/**
 * One image in the gallery. `url` is what the `<img>` renders — a remote URL for
 * an already-saved photo, or a `blob:` object URL for a freshly picked `File`.
 * `file` is set only for new picks (so the submit handler can upload them);
 * `isPrimary` marks the titular photo (the one shown on the product card).
 */
export interface ProductImage {
  id: string;
  url: string;
  file?: File;
  isPrimary?: boolean;
}

const DEFAULT_ACCEPT = 'image/jpeg,image/jpg';

/**
 * How many photos the gallery holds — one cover plus four extras.
 *
 * The design (Figma 838:16558-16568) draws the slots literally rather than as a
 * grid that grows, so the cap is a property of the LAYOUT, not just a policy:
 * there is nowhere to render a sixth tile.
 */
export const MAX_IMAGES = 5;

/** Small slots — everything after the cover. */
const SMALL_SLOTS = MAX_IMAGES - 1;

/** Dashed empty slot; the shared look of every unfilled tile. */
const EMPTY_SLOT =
  'flex items-center justify-center bg-card text-foreground transition-colors ' +
  'outline-none hover:cursor-pointer hover:bg-muted/60 focus-visible:ring-[3px] focus-visible:ring-ring/40';

/**
 * Multi-image gallery upload (Figma 838:16554). Five slots: a square cover drop
 * zone on the left and a 2×2 block of small slots beside it, each drawn as a
 * white dashed tile with an image-add glyph until it holds a photo.
 *
 * The Figma sizes (228px cover, 104px smalls) are the DESKTOP measurements and
 * are applied from `sm` up. Below that they are fluid: hardcoding them put the
 * gallery at ~460px inside a modal barely 360px wide, so the small slots were
 * pushed out of the sheet and the block read as broken rather than as a grid.
 * The cover takes a fixed fraction of the row and the smalls share the rest, so
 * the 1 + 2×2 arrangement survives all the way down.
 *
 * The slots are positional, and position carries meaning — the big one IS the
 * cover. That is why there is no "make titular" star: promoting a photo means
 * moving it into the cover slot, which `makeCover` does by swapping the two
 * entries so the displaced photo keeps its place in the gallery rather than
 * being pushed down and renumbering everything after it.
 *
 * The RHF field value stays a `ProductImage[]` in slot order, with `isPrimary`
 * on index 0 — unchanged from the previous grid, so `toProductInput`'s
 * cover handling and the upload pipeline needed no changes.
 *
 * Object URLs for `File` picks are created on selection and revoked when the
 * item is removed or the component unmounts, so no blob leaks.
 */
export function FormFieldImageGrid({
  accept = DEFAULT_ACCEPT,
  uploadLabel,
  removeLabel = 'Remove',
  primaryLabel = 'Set as cover',
  maxBytes = MAX_IMAGE_BYTES,
  onRejected,
  className,
}: {
  accept?: string;
  /** Prompt inside the empty cover slot (e.g. "Загрузить фото"). */
  uploadLabel: React.ReactNode;
  /** Accessible label for a tile's delete button. */
  removeLabel?: string;
  /** Accessible label for a filled small tile's "promote to cover" action. */
  primaryLabel?: string;
  /** Size ceiling per file, in bytes. Defaults to the shared 1 MB limit. */
  maxBytes?: number;
  /**
   * Called with the names of files rejected for exceeding `maxBytes`. The grid
   * stays presentational — it drops the oversized picks and hands their names
   * to the caller, which owns how that is surfaced (a toast, in the app).
   */
  onRejected?: (fileNames: string[]) => void;
  className?: string;
}) {
  const { field, formItemId } = useFormFieldContext();
  const inputRef = React.useRef<HTMLInputElement>(null);
  const images = normalizeImages(field.value);

  // Track object URLs we created so they can be revoked on unmount — avoids a
  // blob leak when the modal closes without submitting.
  const createdUrls = React.useRef<Set<string>>(new Set());
  React.useEffect(() => {
    const created = createdUrls.current;
    return () => {
      created.forEach((url) => URL.revokeObjectURL(url));
      created.clear();
    };
  }, []);

  const addFiles = (list: FileList | null): void => {
    if (!list || list.length === 0) return;

    // Size gate first, so an oversized file never consumes one of the slots
    // and never reaches the presigned PUT (which uploads straight to object
    // storage and would carry the whole file before anything rejected it).
    const { accepted, rejected } = partitionBySize(Array.from(list), maxBytes);
    if (rejected.length > 0) onRejected?.(rejected);
    if (accepted.length === 0) {
      // Clear the input so re-picking the SAME file fires `change` again —
      // otherwise a merchant who shrinks the file and re-selects it sees
      // nothing happen.
      if (inputRef.current) inputRef.current.value = '';
      return;
    }

    // Only take what still fits. Picking six files must not silently drop the
    // gallery's existing photos or overflow the fixed slot layout.
    const room = MAX_IMAGES - images.length;
    if (room <= 0) return;

    const picked = accepted.slice(0, room).map((file, index) => {
      const url = URL.createObjectURL(file);
      createdUrls.current.add(url);
      return { id: `img-${nextSeq(images) + index}`, url, file };
    });

    field.onChange(ensurePrimary([...images, ...picked]));
    if (inputRef.current) inputRef.current.value = '';
  };

  const removeAt = (index: number): void => {
    const target = images[index];
    if (target && createdUrls.current.has(target.url)) {
      URL.revokeObjectURL(target.url);
      createdUrls.current.delete(target.url);
    }
    field.onChange(ensurePrimary(images.filter((_, i) => i !== index)));
  };

  /**
   * Promote the photo at `index` into the cover slot by SWAPPING it with the
   * current cover, so the demoted photo lands in the slot just vacated instead
   * of shifting every tile after it.
   */
  const makeCover = (index: number): void => {
    if (index === 0) return;
    const next = [...images];
    const cover = next[0];
    const promoted = next[index];
    if (!cover || !promoted) return;
    next[0] = promoted;
    next[index] = cover;
    field.onChange(ensurePrimary(next));
  };

  const openPicker = (): void => inputRef.current?.click();
  const isFull = images.length >= MAX_IMAGES;
  const cover = images[0];

  return (
    <div
      className={cn('flex w-full min-w-0 gap-3', className)}
      data-slot="form-field-image-grid"
    >
      {/* Cover slot — square, 2px dash, 20px radius (Figma 838:16558). Fluid
       * below `sm`, then pinned to the design's 228px.
       *
       * `self-start` matters: as a flex child the cover would otherwise stretch
       * to the row's height, which overrides `aspect-square` and renders the
       * slot as a narrow PORTRAIT box instead of the square the design draws.
       *
       * The fluid share is >50% so the cover stays the dominant tile — it is
       * the product's titular photo, and the 2×2 block reading wider than it
       * inverted the visual hierarchy. */}
      <div className="relative aspect-square w-[58%] min-w-0 shrink-0 self-start sm:aspect-auto sm:size-[228px]">
        {cover ? (
          <Tile
            image={cover}
            radius="rounded-[20px]"
            removeLabel={removeLabel}
            onRemove={() => removeAt(0)}
          />
        ) : (
          <button
            type="button"
            onClick={openPicker}
            className={cn(
              EMPTY_SLOT,
              'size-full rounded-[20px] border-2 border-dashed border-input',
            )}
          >
            <span className="flex w-full max-w-[118px] flex-col items-center gap-2 px-2 sm:gap-4">
              <MenuImageAddIcon className="h-[36px] w-[40px] shrink-0" />
              <span className="text-center text-sm font-medium tracking-tight text-foreground sm:text-base">
                {uploadLabel}
              </span>
            </span>
          </button>
        )}
      </div>

      {/* Four slots in a 2×2 block (Figma 838:16562-16568), 104×108 from `sm`.
       * The block is pinned to 220px (2×104 + the 12px gap) rather than left to
       * `flex-1`, and the tiles are 108 not 106 tall, so 2×108 + 12 = 228 lands
       * flush with the cover instead of ending 4px short. */}
      <div className="grid min-w-0 flex-1 grid-cols-2 content-start gap-3 sm:w-[220px] sm:flex-initial">
        {Array.from({ length: SMALL_SLOTS }, (_, i) => {
          const slot = i + 1;
          const image = images[slot];
          return (
            <div
              key={image?.id ?? `slot-${slot}`}
              className="relative aspect-square w-full min-w-0 sm:aspect-auto sm:h-[108px] sm:w-[104px]"
            >
              {image ? (
                <Tile
                  image={image}
                  radius="rounded-[20px]"
                  removeLabel={removeLabel}
                  onRemove={() => removeAt(slot)}
                  onPromote={() => makeCover(slot)}
                  promoteLabel={primaryLabel}
                />
              ) : (
                <button
                  type="button"
                  onClick={openPicker}
                  // Every empty slot opens the picker, and the pick lands in
                  // the first free one. The design draws all five identically,
                  // so a slot past the end is NOT dimmed or disabled — greying
                  // four of five on an empty gallery reads as "broken" when
                  // clicking any of them does exactly what it looks like.
                  className={cn(
                    EMPTY_SLOT,
                    'size-full rounded-[20px] border border-dashed border-input',
                  )}
                >
                  <MenuImageAddIcon className="h-5 w-[22px]" />
                </button>
              )}
            </div>
          );
        })}
      </div>

      <input
        ref={inputRef}
        id={formItemId}
        type="file"
        className="sr-only"
        accept={accept}
        multiple
        disabled={isFull}
        onChange={(e) => addFiles(e.target.files)}
        onBlur={field.onBlur}
      />
    </div>
  );
}

/**
 * A filled slot: the photo, a floating remove chip, and — for the small slots —
 * a click target that promotes it to cover.
 */
function Tile({
  image,
  radius,
  removeLabel,
  onRemove,
  onPromote,
  promoteLabel,
}: {
  image: ProductImage;
  radius: string;
  removeLabel: string;
  onRemove: () => void;
  onPromote?: () => void;
  promoteLabel?: string;
}) {
  return (
    <>
      {onPromote ? (
        <button
          type="button"
          aria-label={promoteLabel}
          onClick={onPromote}
          // `block` matters: a button is inline-block by default, so it sits on
          // a text baseline and the line box adds descender space UNDER it.
          // That pushed the filled tile ~6px taller than its empty neighbours
          // and broke the 2×2 block's square rhythm.
          className={cn(
            'block size-full overflow-hidden outline-none hover:cursor-pointer focus-visible:ring-[3px] focus-visible:ring-ring/40',
            radius,
          )}
        >
          <img src={image.url} alt="" className="size-full object-cover" />
        </button>
      ) : (
        <div className={cn('size-full overflow-hidden', radius)}>
          <img src={image.url} alt="" className="size-full object-cover" />
        </div>
      )}

      <button
        type="button"
        aria-label={removeLabel}
        onClick={onRemove}
        // Inset INSIDE the tile. Hanging it outside (`-top -right`) let the
        // chip escape its slot and land in the gap beside the neighbouring
        // tiles, where it read as a stray floating button rather than as this
        // photo's delete control.
        className="absolute top-2 right-2 z-10 flex size-6 items-center justify-center rounded-full bg-card text-foreground shadow-[0_0_16px_rgba(0,0,0,0.12)] transition-colors outline-none hover:cursor-pointer hover:bg-muted focus-visible:ring-[3px] focus-visible:ring-ring/40"
      >
        <MenuCloseIcon className="size-[18px]" />
      </button>
    </>
  );
}

/** Coerce the RHF value to a ProductImage[] (tolerates undefined/null). */
function normalizeImages(value: unknown): ProductImage[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter(
      (v): v is ProductImage =>
        typeof v === 'object' &&
        v !== null &&
        typeof (v as ProductImage).url === 'string',
    )
    .slice(0, MAX_IMAGES);
}

/**
 * Next free id suffix. Ids only need to be unique among the CURRENT entries —
 * they key the tiles — so this reads the highest numeric suffix in play rather
 * than counting, which would collide after a middle photo is removed.
 */
function nextSeq(images: ProductImage[]): number {
  return (
    images.reduce((max, img) => {
      const n = Number(img.id.replace(/^img-/, ''));
      return Number.isFinite(n) && n > max ? n : max;
    }, 0) + 1
  );
}

/** Guarantee exactly one titular photo — always the one in the cover slot. */
function ensurePrimary(images: ProductImage[]): ProductImage[] {
  return images.map((img, i) => ({ ...img, isPrimary: i === 0 }));
}
